import React, { useState } from "react";
import { useEffect } from "react";

import AirbyteApi from "apis/airbyte";
import {
  Pane,
  PageLoader,
  Typography,
  Steps,
  Toastr,
} from "clearsense-ui/components";
import { useAirbyteState } from "contexts/airbyte";

import Footer from "./Footer";
import Source from "./Source";
import ExistingSource from "./Source/Existing";
import AddSource from "./Source/New";
import Destination from "./Target";

import { INITIAL_REPLICATION_VALUES } from "../constants";
import Transformations from "../Edit/Transformations";
import Streams from "../Streams";

const NewConnection = ({ isOpen, close }) => {
  const [loading, setLoading] = useState();
  const [catalogLoading, setCatalogLoading] = useState();
  const [selectedSource, setSelectedSource] = useState();
  const [selectedDestination, setSelectedDestination] = useState();
  const [streams, setStreams] = useState([]);
  const [isPathReady, setIsPathReady] = useState(false);
  const [isConnectionSetup, setIsConnectionSetup] = useState(false);
  const [sources, setSources] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [existingOrNew, setExistingOrNew] = useState("existing");
  const [connectionType, setConnectionType] = useState("general");
  const [initialStreamValues, setInitialStreamValues] = useState([]);
  const [replication, setReplication] = useState(INITIAL_REPLICATION_VALUES);
  const [operations, setOperations] = useState(true);

  const { workspaceId } = useAirbyteState();

  const serializedJson = [
    {
      name: "Normalization",
      workspaceId,
      operatorConfiguration: {
        operatorType: "normalization",
        normalization: {
          option: "basic",
        },
      },
    },
  ];

  const fetchSources = async () => {
    setLoading(true);
    try {
      const { data } = await AirbyteApi.fetchSources({ workspaceId });
      setSources(
        data.sources.map(source => ({
          value: source.sourceId,
          label: source.name,
        }))
      );
      fetchDestinations();
    } catch (error) {
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDestinations = async () => {
    try {
      const { data } = await AirbyteApi.fetchDestinations({ workspaceId });
      setDestinations(
        data.destinations.map(destination => ({
          value: destination.destinationId,
          label: destination.name,
        }))
      );
    } catch (error) {
      logger.error(error);
    }
  };

  const handleSourceSubmit = values => {
    setSelectedSource(values.existingSource.value);
    handleConnection(values.existingSource.value);
  };

  const handleConnection = async sourceId => {
    const payload = { sourceId: sourceId ?? selectedSource };
    try {
      setCatalogLoading(true);
      const { data } = await AirbyteApi.fetchCatalog(payload);
      const streamNames = data.catalog.streams.map(
        stream => stream.stream.name
      );
      const values = streamNames.reduce(
        (accumulator, index) => ((accumulator[index] = true), accumulator),
        {}
      );
      setInitialStreamValues(values);
      setStreams(data?.catalog?.streams);
    } catch (error) {
      Toastr.error("Error fetching streams");
    } finally {
      setCatalogLoading(false);
    }
  };

  const handleSubmit = async () => {
    const payload = {
      sourceId: selectedSource,
      destinationId: selectedDestination,
      syncCatalog: { streams: streams },
      prefix: "",
      namespaceDefinition: replication?.namespaceDefinition?.value,
      namespaceFormat: replication?.namespaceFormat ?? "${SOURCE_NAMESPACE}",
      schedule: replication?.frequency?.value,
      operations: operations ? serializedJson : [],
      status: "active",
    };
    try {
      setCatalogLoading(true);
      await AirbyteApi.createConnection(payload);
      Toastr.success("Connection created successfully");
    } catch (error) {
      logger.error(error);
    } finally {
      setCatalogLoading(false);
      handleClose();
    }
  };

  const handleChangeType = type => {
    setExistingOrNew(type);
    setStreams([]);
    setIsConnectionSetup(false);
  };

  const handleClose = () => {
    setSelectedSource(null);
    setExistingOrNew("existing");
    setSelectedDestination(null);
    setIsConnectionSetup(false);
    setSources([]);
    setDestinations([]);
    setIsPathReady(false);
    setConnectionType("general");
    setStreams([]);
    setReplication(INITIAL_REPLICATION_VALUES);
    setOperations(true);
    close();
  };

  useEffect(() => {
    if (isOpen) fetchSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (loading) {
    <div className="h-screen">
      <PageLoader />
    </div>;
  }

  return (
    <Pane isOpen={isOpen} onClose={handleClose} size="lg">
      <Pane.Header className="-mt-3">
        <Typography style="h3" type="bold">
          Setup a Connection
        </Typography>
      </Pane.Header>
      <Pane.Body className="block h-full pt-2 pb-4 space-y-6">
        <Steps
          steps={["Source Information", "Target information"]}
          currentStep={connectionType === "general" ? 1 : 2}
        />
        {connectionType === "general" ? (
          <>
            <Source
              handleChangeType={handleChangeType}
              existingOrNew={existingOrNew}
            />
            <div className="w-full">
              {existingOrNew === "existing" ? (
                <ExistingSource
                  handleSourceSubmit={handleSourceSubmit}
                  sources={sources}
                  isStreamPresent={streams?.length !== 0}
                  catalogLoading={catalogLoading}
                />
              ) : (
                <AddSource
                  setLoading={setLoading}
                  sources={sources}
                  setSelectedSource={setSelectedSource}
                  setConnectionType={setConnectionType}
                  selectedSource={selectedSource}
                  catalogLoading={catalogLoading}
                  handleConnection={handleConnection}
                  isStreamPresent={streams?.length !== 0}
                />
              )}
              <Streams
                streams={streams}
                setStreams={setStreams}
                setConnectionType={setConnectionType}
                initialStreamValues={initialStreamValues}
                isConnectionSetup={isConnectionSetup}
                setIsConnectionSetup={setIsConnectionSetup}
              />
            </div>
          </>
        ) : (
          <div className="w-full space-y-6">
            <Destination
              setLoading={setLoading}
              destinations={destinations}
              setSelectedDestination={setSelectedDestination}
              setConnectionType={setConnectionType}
              isPathReady={isPathReady}
              setIsPathReady={setIsPathReady}
              replication={replication}
              setReplication={setReplication}
            />
            {isPathReady && (
              <Transformations
                operations={operations}
                setOperations={setOperations}
              />
            )}
          </div>
        )}
      </Pane.Body>
      <Pane.Footer className="items-center">
        <Footer
          connectionType={connectionType}
          isConnectionSetup={isConnectionSetup}
          setIsConnectionSetup={setIsConnectionSetup}
          setConnectionType={setConnectionType}
          isPathReady={isPathReady}
          submit={handleSubmit}
          close={handleClose}
        />
      </Pane.Footer>
    </Pane>
  );
};

export default NewConnection;
